import React from 'react';
import { Box, Avatar, makeStyles, Theme, Switch } from '@material-ui/core';
import AppConfig from '@config';
import { DefaultProfile } from '@assets';
import {
  CapaQuotationRfqAllowOpenFileStatus,
  CapaQuotationRfqPartner,
  CapaRfqStatus,
  CapaQuotationUserRfqDetail
} from '@generated/graphql';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';

interface UseStyleProps {
  matches?: boolean;
}
interface DrawingFilesPermissionListProps {
  rfqDetail?: CapaQuotationUserRfqDetail;
  publicOpen?: boolean;
  dataList?: CapaQuotationRfqPartner[];
  matches?: boolean;
  onAllowRfqDrawingFileDownload: (
    quotationPartnerId: string,
    permission: boolean,
    organizationName: string
  ) => void;
}

const DrawingFilesPermissionList: React.FC<DrawingFilesPermissionListProps> = ({
  rfqDetail,
  publicOpen,
  dataList = [],
  matches,
  onAllowRfqDrawingFileDownload
}) => {
  const classes = useStyles({ matches });

  return (
    <>
      <div className={classes.title}>
        <CustomizedTypography
          variant="body1"
          color="textPrimary"
          fontWeight="bold"
          gutterBottoms={4}
        >
          다운로드 권한 설정
        </CustomizedTypography>
        {dataList.length === 0 && (
          <CustomizedTypography variant="body2" color="textSecondary">
            다운로드 권한을 요청한 파트너가 없습니다.
          </CustomizedTypography>
        )}
      </div>
      {dataList?.map((item) => {
        const profileId = item?.profileId ?? undefined;

        const profileImg = profileId
          ? `${AppConfig.fileApiUrl}/file/${profileId}`
          : DefaultProfile;

        const organizationName = item.organizationName ?? '';

        const canDownload =
          publicOpen ||
          item.permission === CapaQuotationRfqAllowOpenFileStatus.Allow;

        const quotationPartnerId = item.quotationPartnerId ?? undefined;

        const isConfirmed = [
          CapaRfqStatus.Confirmed,
          CapaRfqStatus.Complete
        ].includes(rfqDetail?.status as CapaRfqStatus);

        const confirmedWithThisPartner =
          rfqDetail?.transaction?.quotationPartnerId === quotationPartnerId;

        const permission = isConfirmed
          ? confirmedWithThisPartner && canDownload
          : canDownload;

        return (
          <Box className={classes.boxRoot} key={quotationPartnerId}>
            <Avatar src={profileImg} className={classes.avatar}>
              <img src={DefaultProfile} alt="profile" />
            </Avatar>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="500"
              noWrap
            >
              {organizationName}
            </CustomizedTypography>
            <div className={classes.switchWrapper}>
              <CustomizedTypography
                variant="caption"
                color={permission ? 'primary' : 'textSecondary'}
              >
                {permission ? '허용됨' : '제한됨'}
              </CustomizedTypography>
              <Switch
                disabled={isConfirmed && !confirmedWithThisPartner}
                checked={permission}
                color="primary"
                onChange={(event, checked) => {
                  if (isConfirmed && !confirmedWithThisPartner) {
                    // 타업체 확정인 경우 다운로드 권한 제한하고 switch 불가하도록 처리
                    return;
                  }
                  if (!publicOpen) {
                    onAllowRfqDrawingFileDownload(
                      quotationPartnerId,
                      checked,
                      organizationName
                    );
                  }
                }}
              />
            </div>
          </Box>
        );
      })}
    </>
  );
};

export default DrawingFilesPermissionList;

const useStyles = makeStyles<Theme, UseStyleProps>(() => {
  return {
    title: {
      padding: ({ matches }) => (matches ? '20px 16px 16px 16px' : 0),
      marginBottom: 12
    },
    boxRoot: {
      display: 'flex',
      alignItems: 'center',
      height: 80,
      borderBottom: `1px solid ${colorSet.gray200}`,
      padding: ({ matches }) => (matches ? 16 : 0)
    },
    avatar: {
      width: 40,
      height: 40,
      '& img': {
        width: '100%',
        height: '100%'
      },
      backgroundColor: 'transparent',
      marginRight: 12
    },
    switchWrapper: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: 'auto'
    }
  };
});
